import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, FormLabel, FormGroup, Tooltip, IconButton, Icon, Box, FormControl, FilledInput, Chip, Button, Switch } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';
import NotFound from '../../common/not-found';
import KardexDetailCreateAdjustment from '../components/kardex-detail-create-adjustment';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';

dayjs.extend(utc);

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    searchBtn: {
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    createBtn: {
        marginRight: 15,
        backgroundColor: '#198754',
        borderColor: '#198754',
        '&:hover': {
            backgroundColor: '#198754',
            borderColor: '#198754',
            boxShadow: 'none'
        }
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 24
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20
    },
}));

const KardexDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const classes = useStyles();
    const [showCreate, setShowCreate] = useState(false);
    const [state, setState] = useState({
        loading: true,
        header: {},
        data: [],
        filter: {
            startDate: dayjs().subtract(7, 'day').startOf('day').format(),
            endDate: dayjs().endOf('day').format(),
            dateRange: `${dayjs().subtract(7, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            transactionDate: true
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(7, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Kardex - Detail';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/invKardex/${id}`, state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                header: response.header,
                data: response.data
            }));
        } else {
            console.log('[KardexDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).startOf('day').format(),
                endDate: dayjs(endDate.toISOString()).endOf('day').format()
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleSwitch = ({ target }) => setState(prev => ({ ...prev, filter: { ...prev.filter, transactionDate: target.checked } }));

    const search = async () => {
        showLoader(true);
        const response = await get(`/invKardex/${id}/search`, state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response.data }));

            if (!response?.data[0]) showToast('There are no data with these filters', 'warning');
        } else {
            console.log('[KardexIndex] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const getTransactionDate = ({ value }) => dayjs(value).utc().local().format('MM/DD/YYYY HH:mm');
    const getTicketDate = ({ row }) => row.ticket ? dayjs(row.ticket.date).format('MM/DD/YYYY') : 'N/A';
    const getUser = ({ value }) => `${value?.names} ${value?.surnames}`;
    const getItem = ({ row }) => {
        if (row?.idticket_process_v2 !== null) return `Process (${row?.process?.item?.name ?? '?'})`;
        if (row?.iddetail_ticket !== null) return `Ticket (${row?.detail?.item?.name ?? '?'})`;

        return '-';
    }
    const renderTicket = ({ row }) => {
        if (!row?.idticket) return '-';
        let linkTo = '/';
        if (row?.iddetail_ticket) linkTo = `/ticket/tickets/${row.idticket}`;
        if (row?.idticket_process_v2) linkTo = { pathname: '/process/processesV2/detail', state: { code: row?.ticket?.code } };

        return (
            <Link to={linkTo}>
                <Chip clickable size='small' label={row?.ticket?.code ?? 'N/A'} />
            </Link>
        );
    }

    const renderCell = ({ row }) => {
        if (row?.note === null) return <></>;

        return (
            <Tooltip title={row?.note} placement='top'>
                <IconButton>
                    <Icon>{'notes'}</Icon>
                </IconButton>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 180, valueGetter: getTransactionDate },
        { field: 'ticket', headerName: 'Ticket', flex: 1, minWidth: 180, renderCell: renderTicket },
        { field: 'ticket_date', headerName: 'Ticket date', flex: 0.75, minWidth: 135, valueGetter: getTicketDate },
        { field: 'item', headerName: 'Item', flex: 0.7, minWidth: 126, valueGetter: getItem },
        { field: 'qty', headerName: 'Qty', flex: 0.75, minWidth: 135, type: 'number' },
        { field: 'balance', headerName: 'Balance', flex: 0.75, minWidth: 135, type: 'number' },
        { field: 'storage_qty', headerName: 'Storage qty', flex: 0.75, minWidth: 135, type: 'number' },
        { field: 'storage_balance', headerName: 'Storage balance', flex: 0.75, minWidth: 135, type: 'number' },
        { field: 'type', headerName: 'Move', flex: 0.6, minWidth: 108 },
        { field: 'user', headerName: 'User', flex: 1, minWidth: 180, valueGetter: getUser },
        { field: 'actions', headerName: 'Actions', flex: 0.5, minWidth: 100, renderCell }
    ]

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr || !state.header?.idproduct_type_ref) return <NotFound />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Product'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Product'
                                value={state.header?.product?.name ?? '?'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Type'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                placeholder='Type'
                                value={state.header?.type?.name ?? '?'}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={2} xl={1}>
                    <FormGroup style={{ alignItems: 'center' }}>
                        <FormLabel>{'Search'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                            style={{ alignItems: 'center', marginTop: 8 }}
                        >
                            <Switch
                                onChange={handleSwitch}
                                checked={state.filter.transactionDate}
                            />
                            <FormLabel>{state.filter.transactionDate ? 'Transaction date' : 'Ticket date'}</FormLabel>
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                {
                    permissions.create_attr &&
                    <Button
                        variant='contained'
                        className={classes.createBtn}
                        onClick={() => setShowCreate(!showCreate)}
                    >
                        {'Create adjusment'}
                    </Button>
                }

                <Button
                    variant='contained'
                    className={classes.searchBtn}
                    onClick={search}
                >
                    {'Search'}
                </Button>
            </div>

            <KardexDetailCreateAdjustment
                id={id}
                open={showCreate}
                onClose={() => {
                    search();
                    setShowCreate(false);
                }}
                permissions={state.permissions}
            />

            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    disableSelectionOnClick
                    pageSize={45}
                    density='compact'
                    rows={state.data}
                    columns={columns}
                    getRowId={row => row.idinv_kardex_activity}
                />
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />

        </div >
    );
}

export default KardexDetail;